import { Form, Spin } from 'antd';
import RadioCards from 'components/RadioCards/RadioCards';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { getNextStep, getPreviousStep, requiresAdditionalParent, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';
import { Banner } from 'components/ui/Banner';
import { formatAddress } from 'components/FormattedAddress/FormattedAddress';
import { getFirstName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationChildrenLivingSituation({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  // Form state
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Get entity index from query parameters
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const currentChild = application?.children?.[currentChildIndex];
  const childFirstName = getFirstName({ firstName: currentChild?.firstName });

  useEffect(() => {
    if (currentChild) {
      form.setFieldsValue({
        livingSituation: currentChild.livingSituation,
      });
    }
  }, [currentChild, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Navigate to next step
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION,
          _newApplication,
          stepConfig,
          currentChildIndex,
        );
        setStep({
          applicationId: _newApplication.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const childData = {
          ...currentChild,
          livingSituation: values.livingSituation,
        };

        // Update child in household
        const savedChild = await familyService.addUpdateFamilyChild(household?.id, currentChild?.id || null, childData);

        // Update application with the saved child
        const updatedChildren = [...(application.children || [])];
        updatedChildren[currentChildIndex] = savedChild;

        const updatedApplication = {
          ...application,
          children: updatedChildren,
        };

        const savedApplication = await familyService.saveApplication(updatedApplication);
        setApplication(savedApplication);

        _goToNextStep(savedApplication);
      } catch (error) {
        console.error('Error saving child living situation:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, currentChild, currentChildIndex, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  const livingSituationOptions = useMemo(() => {
    const applicant = application?.applicant;
    const otherParent = application?.additionalParents?.[0];

    return [
      {
        value: 'WithApplicant',
        label: isCompletedBySponsor ? 'With me at:' : 'With the service member at:',
        description: formatAddress(applicant?.homeAddress),
      },
      ...(requiresAdditionalParent(application) && otherParent?.addressSameAsApplicant !== true
        ? [
            {
              value: 'WithOtherParent',
              label: isCompletedBySponsor ? 'With Other Parent at:' : 'With me at:',
              description: formatAddress(otherParent?.homeAddress ?? null),
            },
            {
              value: 'Shared',
              label: 'Splits time between households',
            },
          ]
        : []),
      {
        value: 'Other',
        label: 'With someone else',
      },
    ];
  }, [application, isCompletedBySponsor]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Living Situation`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-6">
            <Banner question={`Where does ${childFirstName} live?`} className="my-12" />

            <Form.Item
              name="livingSituation"
              rules={[{ required: true, message: 'Please select where the child lives' }]}
            >
              <RadioCards options={livingSituationOptions} data-testid="child-living-situation-cards" />
            </Form.Item>
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_LIVING_SITUATION,
                application,
                stepConfig,
                currentChildIndex,
              );
              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
            disabled: submitting,
            'data-testid': 'children-living-situation-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'children-living-situation-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
